import type { CalendarEvent } from '../../lib/icsCalendar'
import {
  DAY_NAMES_CZ_SHORT,
  MONTH_NAMES_CZ,
  getEventsForDayInMonth,
  getMonthGridMeta,
  isMultiDayEvent,
} from '../../lib/calendarMonthUtils'

type Props = {
  year: number
  month: number
  events: CalendarEvent[]
  loading?: boolean
  emptyHint?: string
  accent?: 'teal' | 'violet'
  onDayClick?: (day: number, dayEvents: CalendarEvent[]) => void
}

export function CabinetMonthGrid({
  year,
  month,
  events,
  loading,
  emptyHint,
  accent = 'teal',
  onDayClick,
}: Props) {
  const { daysInMonth, adjustedFirstDay } = getMonthGridMeta(year, month)
  const today = new Date()
  const isToday = (day: number) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear()

  const accentClass = accent === 'violet' ? 'cabinet-month-grid--violet' : 'cabinet-month-grid--teal'

  if (loading) {
    return (
      <div className={`cabinet-month-grid ${accentClass} cabinet-month-grid--loading`}>
        <div className="cabinet-month-grid__loading">Načítání…</div>
      </div>
    )
  }

  return (
    <div className={`cabinet-month-grid ${accentClass}`}>
      <div className="cabinet-month-grid__head">
        {DAY_NAMES_CZ_SHORT.map((d) => (
          <div key={d} className="cabinet-month-grid__dow">
            {d}
          </div>
        ))}
      </div>
      <div className="cabinet-month-grid__body">
        {Array.from({ length: adjustedFirstDay }).map((_, i) => (
          <div key={`e-${i}`} className="cabinet-month-grid__cell cabinet-month-grid__cell--empty" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1
          const dayEvents = getEventsForDayInMonth(events, year, month, day)
          const hasMulti = dayEvents.some((e) => isMultiDayEvent(e))
          return (
            <button
              key={day}
              type="button"
              className={[
                'cabinet-month-grid__cell',
                isToday(day) ? 'cabinet-month-grid__cell--today' : '',
                dayEvents.length > 0 ? 'cabinet-month-grid__cell--has' : '',
                hasMulti ? 'cabinet-month-grid__cell--multi' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onDayClick?.(day, dayEvents)}
              title={
                dayEvents.length
                  ? `${day}. ${MONTH_NAMES_CZ[month]} — ${dayEvents.length} položek`
                  : `${day}. ${MONTH_NAMES_CZ[month]}`
              }
            >
              <span className="cabinet-month-grid__daynum">{day}</span>
              {dayEvents.length > 0 && (
                <span className="cabinet-month-grid__badge">{dayEvents.length}</span>
              )}
            </button>
          )
        })}
      </div>
      {events.length === 0 && emptyHint && (
        <p className="cabinet-month-grid__hint">{emptyHint}</p>
      )}
    </div>
  )
}
